import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useContext,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle
} from 'react';
import * as THREE from 'three';
// import randomColor from 'randomcolor';
import { useFrame, useThree } from '@react-three/fiber';

import { DesignContext } from '../contexts/designContext.js';

const DEFAULT_SETBACK_COLOR = 'orange';
const DEFAULT_SETBACK_OPACITY = 0.5;
const DEFAULT_SETBACK_SELECTED_OPACITY = 0.8;

// const points = [];

// const createPlusTexture = () => {
//   const canvas = document.createElement('canvas');
//   canvas.width = 128;
//   canvas.height = 128;
//   const ctx = canvas.getContext('2d');

//   ctx.fillStyle = '#00FF00';
//   ctx.fillRect(0, 0, 128, 128);

//   ctx.strokeStyle = '#000000';
//   ctx.lineWidth = 4;

//   ctx.beginPath();
//   ctx.moveTo(32, 16);
//   ctx.lineTo(32, 48);
//   ctx.stroke();

//   ctx.beginPath();
//   ctx.moveTo(16, 32);
//   ctx.lineTo(48, 32);
//   ctx.stroke();

//   return new THREE.CanvasTexture(canvas);
// };

// const panelUVGenerator = {
//   generateTopUV: function(geometry, vertices, indexA, indexB, indexC) {
//     return [
//       new THREE.Vector2(0, 0),
//       new THREE.Vector2(1, 0),
//       new THREE.Vector2(1, 1)
//     ];
//   },
//   generateSideWallUV: function(geometry, vertices, indexA, indexB, indexC, indexD) {
//     return [
//       new THREE.Vector2(0, 1),
//       new THREE.Vector2(1, 1),
//       new THREE.Vector2(0, 0),
//       new THREE.Vector2(1, 0)
//     ];
//   }
// };

const getMaterial = (name, color, opacity, standard, side, texture = null) => {
  if (standard)
    return (
      <meshStandardMaterial
        name={name}
        attach="material"
        color={color != '' ? color : 'rgb(255,255,255)'}
        wireframe={false}
        side={side}
        transparent={opacity < 1}
        opacity={opacity != 1 ? opacity : 1}
        map={texture}
        castShadow
        receiveShadow
      />
    );
  return (
    <meshBasicMaterial
      name={name}
      attach="material"
      color={color != '' ? color : 'rgb(255,255,255)'}
      transparent={opacity < 1}
      opacity={opacity != 1 ? opacity : 1}
      wireframe={false}
      side={side}
      map={texture}
    />
  );
};

const Roof = forwardRef(
  (
    {
      index = -1,
      vertices,
      vertices3D,
      uv,
      eave,
      panelEave = null,
      type,
      pitch = 0,
      azimuth = 0,
      eaveHeight = 0,
      selectable = false,
      isSelected = false,
      panels = null,
      setbacks = null,
      setbacks3D = null,
      shapeType,
      getParentRoof,
      transformation
    },
    forwardedRef
  ) => {
    const {
      texture,
      selectedTexture,
      setback,
      removeFromSelectedObjects,
      addToSelectedObjects,
      selectedObjects,
      showSunLight,
      showPanels,
      panelSelection,
      insertPanels,
      showSetbacks,
      setbackSelection,
      moveSelection,
      insertObject,
      showVirtualPanels,
      accordion,
      moveStatus,
      readonly = false,
      allowPanelToggleInReadonly = true,
      viewerMode = false,
      panelVisibility = false,
      viewState,
      panelPlacementFillMode,
      panelPlacementOnlySelectedRoof
    } = useContext(DesignContext);

    const [hoveredPanel, setHoveredPanel] = useState(null);
    const mesh = useRef(null);
    const geometry = useRef();
    const panelRefs = React.useRef([]);
    const setBackRefs = React.useRef([]);
    const shape = useMemo(() => new THREE.Shape(vertices), [vertices]);
    // const rndColor = useMemo(() => randomColor(), []);
    const isRoof = type === 'roof';
    const isObstacle = type === 'obstacle';
    const tilt = panels?.tilt ?? 0;
    const lineShape = shapeType === 'Line';

    const setbackShapes = useMemo(() => {
      return setbacks?.map((vs) => new THREE.Shape([...vs]));
      // if (isObstacle) return borderPolygon(vertices, Math.min(1, Math.abs(fireSetbacks)), Math.abs(fireSetbacks), fireSetbacks > 0).map((vs) => new THREE.Shape([...vs]));
      // return shrinkPolygon(vertices, fireSetbacks ?? vertices.map((v, vi) => setback))
      //   .map((vs) => new THREE.Shape([...vs]));
    }, [setbacks]);

    const [selected, setSelected] = useState(false);

    useEffect(() => {
      if (panels && panels.projectedPoints) panelRefs.current = panelRefs.current.slice(0, panels.projectedPoints.length);
    }, [panels]);

    useEffect(() => {
      setBackRefs.current = setBackRefs.current.slice(0, vertices.length);
    }, [vertices]);

    const [hasEave, , eaveDirection, mx, mxr, eh] = transformation;
    const panelRenderEave = panelEave ?? eave;
    const panelEaveDirection = useMemo(() => {
      const [a, b] = panelRenderEave ?? [];
      return a && b ? b.clone().sub(a).normalize() : eaveDirection;
    }, [panelRenderEave, eaveDirection]);
    const panelMxpanel = useMemo(() => {
      const qp = new THREE.Quaternion().setFromAxisAngle(panelEaveDirection, tilt * (Math.PI / 180.0));
      return new THREE.Matrix4().makeRotationFromQuaternion(qp);
    }, [panelEaveDirection, tilt]);

    // const [hasEave, eaveType, eaveDirection, mx, mxr, eh, mxpanel] = useMemo(() => {
    //   const [a, b, eaveTypex] = eave;
    //   const hasEavex = a.clone().sub(b).length() > 0;
    //   // const hasEavex = eave && eave[1];
    //   let ehx = eaveHeight;
    //   if (isRoof && ehx === 0 && pitch === 0) ehx = 0.5;
    //   const eaveDirectionx = hasEavex ? b.clone().sub(a).normalize() : new THREE.Vector3(1, 0, 0);
    //   // const eaveTypex = eave[2];
    //   // const a = eave[0];
    //   if (isRoof && hasEavex) {
    //     const z = new THREE.Vector3(0, 0, 1);
    //     const u = b.clone().sub(a).cross(z).normalize();
    //     const q = new THREE.Quaternion().setFromAxisAngle(eaveDirectionx, pitch * (Math.PI / 180.0) * (false && eaveTypex === 'ridge' ? -1 : 1));
    //     const qroll = new THREE.Quaternion().setFromAxisAngle(u, 0 * (Math.PI / 180.0));
    //     const mxx = new THREE.Matrix4().makeTranslation(-a.x, -a.y, -a.z).premultiply(
    //       new THREE.Matrix4().makeRotationFromQuaternion(qroll).premultiply(
    //         new THREE.Matrix4().makeRotationFromQuaternion(q).premultiply(
    //           new THREE.Matrix4().makeTranslation(a.x, a.y, a.z).premultiply(
    //             new THREE.Matrix4().makeTranslation(0, 0, -ehx),
    //           ),
    //         ),
    //       ),
    //     );
    //     const qp = new THREE.Quaternion().setFromAxisAngle(eaveDirectionx, tilt * (Math.PI / 180.0));
    //     const mxpanel = new THREE.Matrix4().makeRotationFromQuaternion(qp);
    //     return [hasEavex, eaveTypex, eaveDirectionx, mxx, null, ehx, mxpanel];
    //   }

    //   const mxx = new THREE.Matrix4().makeTranslation(0, 0, -ehx);
    //   return [hasEavex, eaveTypex, eaveDirectionx, mxx, null, ehx];
    //   // if (eaveTypex === 'ridge') {
    //   //   const v3 = vertices.map((v) => -v.clone().applyMatrix4(mxx).z);
    //   //   const mh = ehx - Math.min(...v3);
    //   //   const mxxr = new THREE.Matrix4().makeTranslation(0, 0, -mh);
    //   //   return [hasEavex, eaveTypex, eaveDirectionx, mxx, mxxr, ehx];
    //   // }
    // }, [eave, eaveHeight, isRoof, pitch, azimuth, tilt]);

    const panelShapes = useMemo(() => {
      if (!isRoof) return null;
      if (!panelRenderEave) return null;
      const [a, b, , ai, bi] = panelRenderEave;
      if (!a || !b || !vertices?.[ai] || !vertices?.[bi]) return null;
      const azimuthDir = b.clone().sub(a).normalize();
      const edgeDir = vertices[bi].clone().sub(vertices[ai]).normalize();
      const alpha = Math.atan2(azimuthDir.y, azimuthDir.x) - Math.atan2(edgeDir.y, edgeDir.x); // azimuthDir.angleTo(edgeDir);
      const mrotate = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 0, 1), -alpha);
      const hs = 1; //panels?.scale ?? 1;
      const shapes =
        panels && panels.projectedPoints
          ? panels.projectedPoints.map(
              ([, , w, h]) =>
                new THREE.Shape([
                  new THREE.Vector3(0, 0).applyMatrix4(mrotate),
                  new THREE.Vector3(0, h).applyMatrix4(mrotate),
                  new THREE.Vector3(-w * hs, h).applyMatrix4(mrotate),
                  new THREE.Vector3(-w * hs, 0).applyMatrix4(mrotate)
                ])
            )
          : null;
      return shapes;
    }, [isRoof, panelRenderEave, panels, vertices]);

    const insertingMarkerObjects = useMemo(() => {
      if (!panels?.samples3D) return null;

      const si = panelPlacementFillMode == 1 || panelPlacementFillMode == 4 ? 0 : 1;

      const sample = panels.samples3D[si];
      if (!sample || !Array.isArray(sample) || sample.length < 4) return null;

      const [v0, v1, v2, v3] = sample;

      const geometry = new THREE.BufferGeometry();

      const vertices = new Float32Array([
        v0.x - v0.x,
        v0.z - v0.z,
        v0.y - v0.y,
        v1.x - v0.x,
        v1.z - v0.z,
        v1.y - v0.y,
        v2.x - v0.x,
        v2.z - v0.z,
        v2.y - v0.y,
        v3.x - v0.x,
        v3.z - v0.z,
        v3.y - v0.y
      ]);

      geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

      const indices = [0, 1, 2, 0, 2, 3];
      geometry.setIndex(indices);

      geometry.computeVertexNormals();

      const material = new THREE.MeshStandardMaterial({
        color: 'green',
        side: THREE.DoubleSide
      });
      const mesh = new THREE.Mesh(geometry, material);

      return mesh;
    }, [panels?.samples3D, panelPlacementFillMode]);

    useLayoutEffect(() => {
      if (!mesh.current) return;
      mesh.current.matrix.identity();
      // if (isRoof && hasEave) {
      mesh.current.applyMatrix4(mx);
      if (mxr) mesh.current.applyMatrix4(mxr);
      // } else {
      //   mesh.current.applyMatrix4(new THREE.Matrix4().makeTranslation(0, 0, -eh));
      // }
    }, [eh, hasEave, isRoof, mx, mxr, vertices]);

    useEffect(() => {
      if (!mesh.current) return;

      const applyMatrix = (mxx) => {
        if (setBackRefs?.current)
          setBackRefs.current.forEach((sbRef) => {
            if (sbRef?.applyMatrix4) sbRef.applyMatrix4(mxx);
          });
      };

      if (setBackRefs?.current) {
        setBackRefs.current.forEach((sbRef, ri) => {
          if (sbRef?.matrix) {
            sbRef.matrix.identity();
            sbRef.position.set(0, 0, -0.02);
            sbRef.rotation.set(0, 0, 0);
            sbRef.updateMatrix();
          }
        });
      }

      if (hasEave) {
        applyMatrix(mx);
        if (mxr) applyMatrix(mxr);
      } else {
        applyMatrix(new THREE.Matrix4().makeTranslation(0, 0, -eh));
      }
    }, [eh, hasEave, isRoof, mx, mxr, setbacks]);

    useLayoutEffect(() => {
      if (!mesh.current) return;

      // points.forEach((p)=> { scene.remove(p); });
      // points.length = 0;

      const applyMatrix = (mxx) => {
        if (panelRefs?.current)
          panelRefs.current.forEach((pRef) => {
            if (pRef?.applyMatrix4) {
              // const px = mxx.clone();// .premultiply(new THREE.Matrix4().makeTranslation(0, 0, -5));
              pRef.applyMatrix4(mxx);
              // const geometry = new THREE.SphereGeometry(0.5, 12, 8);
              // const material = new THREE.MeshBasicMaterial({ color: 0xff0000 });
              // const sphere = new THREE.Mesh(geometry, material);
              // sphere.position.set(pRef.position.x, -pRef.position.z, pRef.position.y);
              // points.push(shape);
              // scene.add(sphere);
            }
          });
      };

      if (panelRefs?.current) {
        if (!panelRenderEave) return;
        const [, , eaveTypex] = panelRenderEave;
        let ehx = eaveHeight;
        if (isRoof && ehx === 0 && pitch === 0) ehx = 0.5;
        const isRidge = eaveTypex === 'ridge';

        panelRefs.current.forEach((pRef, refIndex) => {
          if (pRef?.matrix) {
            const [x, y, w, h] = panels.projectedPoints[refIndex];
            const ty = panels.points[refIndex][4];
            const o = ((ty === 'h' ? h : w) + 0.5) * Math.sin((tilt / 180.0) * Math.PI);

            pRef.matrix.identity();
            const panelTr = new THREE.Matrix4()
              .makeRotationZ(Math.atan2(panelEaveDirection.y, panelEaveDirection.x) + Math.PI / 2)
              .premultiply(panelMxpanel)
              .premultiply(new THREE.Matrix4().makeTranslation(x, y, -0.21 + (isRidge ? -o : 0)));
            pRef.applyMatrix4(panelTr);

            // pRef.matrix.identity();
            // pRef.position.set(x, y, -0.21);
            // pRef.updateMatrix();
            // const mold1 = pRef.matrix.clone();
            // pRef.matrix.identity();
            // pRef.rotation.set(0, 0, Math.atan2(eaveDirection.y, eaveDirection.x) + Math.PI / 2);
            // pRef.updateMatrix();
            // const mold2 = pRef.matrix.clone();
            // pRef.matrix.identity();
            // pRef.applyMatrix4(mold2);
            // pRef.applyMatrix4(mold1);
            // pRef.applyMatrix4(new THREE.Matrix4().makeTranslation(0, 0, -5));
          }
        });
      }

      if (isRoof && hasEave) {
        applyMatrix(mx);
        if (mxr) applyMatrix(mxr);
      } else applyMatrix(new THREE.Matrix4().makeTranslation(0, 0, -eh));
    }, [
      eaveDirection.x,
      eaveDirection.y,
      panelEaveDirection.x,
      panelEaveDirection.y,
      eh,
      hasEave,
      isRoof,
      mx,
      mxr,
      panels,
      panelMxpanel,
      eave,
      panelRenderEave,
      eaveHeight
    ]);

    // useLayoutEffect(() => {
    //   if (!mesh.current) return;

    //   let eh = eaveHeight;
    //   if (isRoof && eh === 0 && pitch === 0) eh = 0.5;

    //   const applyMatrix = (mx) => {
    //     mesh.current.applyMatrix4(mx);
    //     if (panelRefs?.current) panelRefs.current.forEach((pRef) => { if (pRef?.applyMatrix4) pRef.applyMatrix4(mx); });
    //     if (setBackRefs?.current) setBackRefs.current.forEach((sbRef) => { if (sbRef?.applyMatrix4) sbRef.applyMatrix4(mx); });
    //   };

    //   const eaveDirection = hasEave ? eave[1].clone().sub(eave[0]).normalize() : new THREE.Vector3(1, 0, 0);

    //   mesh.current.matrix.identity();
    //   if (panelRefs?.current) {
    //     panelRefs.current.forEach((pRef, refIndex) => {
    //       if (pRef?.matrix) {
    //         const [x, y] = panels.projectedPoints[refIndex];
    //         pRef.matrix.identity();
    //         pRef.position.set(x, y, -0.21);
    //         pRef.rotation.set(0, 0, Math.atan2(eaveDirection.y, eaveDirection.x) + Math.PI / 2);
    //         pRef.updateMatrix();
    //       }
    //     });
    //   }

    //   if (setBackRefs?.current) {
    //     setBackRefs.current.forEach((sbRef, ri) => {
    //       if (sbRef?.matrix) {
    //         sbRef.matrix.identity();
    //         sbRef.position.set(0, 0, -0.1 - (0.01 * ri));
    //         sbRef.rotation.set(0, 0, 0);
    //         sbRef.updateMatrix();
    //       }
    //     });
    //   }

    //   if (isRoof && hasEave) {
    //     const eaveType = eave[2];
    //     const point = eave[0];
    //     const q = new THREE.Quaternion().setFromAxisAngle(eaveDirection, pitch * (Math.PI / 180.0) * (eaveType === 'ridge' ? -1 : 1));
    //     const mx = new THREE.Matrix4().makeTranslation(-point.x, -point.y, -point.z).premultiply(
    //       new THREE.Matrix4().makeRotationFromQuaternion(q).premultiply(
    //         new THREE.Matrix4().makeTranslation(point.x, point.y, point.z).premultiply(
    //           new THREE.Matrix4().makeTranslation(0, 0, -eh),
    //         ),
    //       ),
    //     );

    //     applyMatrix(mx);
    //     if (eaveType === 'ridge') {
    //       const v3 = vertices.map((v) => -v.clone().applyMatrix4(mx).z);
    //       const mh = Math.max(...v3) - Math.min(...v3);
    //       applyMatrix(new THREE.Matrix4().makeTranslation(0, 0, -mh));
    //     }
    //   } else {
    //     applyMatrix(
    //       new THREE.Matrix4().makeTranslation(0, 0, -eh),
    //     );
    //   }
    // }, [eave, pitch, eaveHeight, panels, fireSetbacks]);

    useEffect(() => {
      if (!selectable) {
        if (selected) setSelected(false);
        return;
      }
      if (selectable && isSelected !== selected) setSelected(isSelected);
    }, [selectable, isSelected, selected, index]);

    // useEffect(() => {
    //   if (selectable) {
    //     const newSelected = [...selectedSetbacks];
    //     selectedObjects.fireSetbacks.forEach((ssb) => {
    //       const [ri, sbi] = ssb.split('-');
    //       if (ri === index) selectedSetbacks[sbi] = true;
    //     });
    //     setSelected(isSelected);
    //   }
    // }, [selectedObjects.fireSetbacks]);

    useEffect(() => {
      if (geometry.current) geometry.current.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(uv), 2));
    }, [uv]);

    // useEffect(() => {
    //   setTimeout(() => {
    //     document.body.style.cursor = hoveredPanel !== null ? 'pointer' : 'default';
    //   }, 100);
    //   // return () => {
    //   //   document.body.style.cursor = 'default';
    //   // };
    // }, [hoveredPanel]);

    const changeSelection = (e, selection) => {
      if (selectable && !insertObject && !readonly && viewState != 'annotations') {
        e.stopPropagation();
        setSelected(selection);
        const isRevise = accordion === 'revise';

        if (selection) addToSelectedObjects(index, 'roofSegments', isRevise);
        else removeFromSelectedObjects(index, 'roofSegments');
      }
    };

    const setbackChangeSelection = (e, sbi, selection) => {
      if (setbackSelection && !readonly && viewState != 'annotations') {
        e.stopPropagation();
        if (isRoof) {
          if (selection) addToSelectedObjects(`${index}-${sbi}`, 'fireSetbacks');
          else removeFromSelectedObjects(`${index}-${sbi}`, 'fireSetbacks');
        } else {
          setbackShapes.map((sb, sbi) => {
            if (selection) addToSelectedObjects(`${index}-${sbi}`, 'fireSetbacks');
            else removeFromSelectedObjects(`${index}-${sbi}`, 'fireSetbacks');
          });
        }
      }
    };

    const panelChangeSelection = (e, pi, selection) => {
      // When allowPanelToggleInReadonly is false (e.g. designer homeowner), only allow click when selectionMode is 'panels'. When true or not passed, keep current behavior.
      const enabled = panelSelection || (allowPanelToggleInReadonly && readonly && (panelVisibility || !selection));
      if (enabled) e.stopPropagation();
      if (!viewerMode && enabled && !insertObject && viewState != 'annotations' && (moveStatus === 0 || moveStatus === 1)) {
        if (selection) addToSelectedObjects(`${index}-${pi}`, 'panels');
        else removeFromSelectedObjects(`${index}-${pi}`, 'panels');
      }
    };

    const matSelected = useMemo(
      () => getMaterial('', 'rgb(255,150,150)', 1, showSunLight, THREE.DoubleSide, selectedTexture),
      [selectedTexture, showSunLight]
    );
    const matFront = useMemo(() => getMaterial('', '', 1, showSunLight, THREE.BackSide, texture), [texture, showSunLight]);
    const matRoof = useMemo(() => getMaterial('', '', 1, showSunLight, THREE.DoubleSide, texture), [texture, showSunLight]);

    const matBack = useMemo(() => {
      const mb = getMaterial('', 'rgb(250,0,0)', 1, showSunLight, THREE.FrontSide, null);
      return [matFront, mb];
    }, [matFront, showSunLight]);

    const panelMat = useMemo(
      () => getMaterial('mat_panel', 'rgb(0,0,0)', 0.2, showSunLight, THREE.FrontSide),
      [showSunLight]
    );
    const selectedPanelMat = useMemo(
      () => getMaterial('mat_selected_panel', 'rgb(0,0,0)', 0.7, showSunLight, THREE.FrontSide),
      [showSunLight]
    );
    const virtualPanelMat = useMemo(
      () => getMaterial('virtual_mat_panel', 'rgb(0,255,0)', 0.1, showSunLight, THREE.FrontSide),
      [showSunLight]
    );
    const selectedVirtualPanelMat = useMemo(
      () => getMaterial('virtual_mat_selected_panel', 'rgb(0,255,0)', 0.3, showSunLight, THREE.FrontSide),
      [showSunLight]
    );
    const invalidPanelMat = useMemo(
      () => getMaterial('mat_panel', 'rgb(255, 0, 0)', 0.2, showSunLight, THREE.FrontSide),
      [showSunLight]
    );
    const invalidSelectedPanelMat = useMemo(
      () => getMaterial('mat_selected_panel', 'rgba(128, 0, 0, 1)', 0.7, showSunLight, THREE.FrontSide),
      [showSunLight]
    );
    const alertedPanelMat = useMemo(
      () => getMaterial('mat_panel', 'rgb(255, 110, 0)', 0.2, showSunLight, THREE.FrontSide),
      [showSunLight]
    );
    const alertedSelectedPanelMat = useMemo(
      () => getMaterial('mat_selected_panel', 'rgba(100, 50, 0, 1)', 0.7, showSunLight, THREE.FrontSide),
      [showSunLight]
    );

    // if (!hasEave && !selectable) return null;

    const createSetbackHatchMaterial = (
      opacity,
      hatchEnabled = true,
      hatchBothDirections = false,
      color = DEFAULT_SETBACK_COLOR
    ) => {
      if (!hatchEnabled) {
        return new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity,
          side: THREE.DoubleSide,
          depthTest: true,
          depthWrite: false,
          depthFunc: THREE.LessEqualDepth,
          blending: THREE.AdditiveBlending
        });
      }

      return new THREE.ShaderMaterial({
        transparent: true,
        uniforms: {
          baseColor: { value: new THREE.Color(color) },
          hatchColor: { value: new THREE.Color('white') },
          opacity: { value: opacity },
          spacing: { value: 1.0 },
          thickness: { value: 0.1 },
          hatchBothDirections: { value: hatchBothDirections ? 1.0 : 0.0 }
        },
        vertexShader: `
          varying vec2 vWorldXZ;
          void main() {
            vec4 worldPos = modelMatrix * vec4(position, 1.0);
            vWorldXZ = worldPos.xz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 baseColor;
          uniform vec3 hatchColor;
          uniform float opacity;
          uniform float spacing;
          uniform float thickness;
          uniform float hatchBothDirections;
          varying vec2 vWorldXZ;

          float hatchLine(float v) {
            float m = mod(v, spacing);
            float d = min(m, spacing - m);
            return 1.0 - smoothstep(thickness, thickness + 0.002, d);
          }

          void main() {
            float l1 = hatchLine(vWorldXZ.x + vWorldXZ.y);
            float l2 = hatchLine(vWorldXZ.x - vWorldXZ.y) * hatchBothDirections;
            float mask = max(l1, l2);
            vec3 col = mix(baseColor, hatchColor, mask);
            gl_FragColor = vec4(col, opacity);
          }
        `,
        side: THREE.DoubleSide,
        depthTest: true,
        depthWrite: false,
        depthFunc: THREE.LessEqualDepth,
        blending: THREE.CustomBlending,
        blendSrc: THREE.SrcAlphaFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor,
        blendEquation: THREE.AddEquation,
        blendSrcAlpha: THREE.SrcAlphaFactor,
        blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
        blendEquationAlpha: THREE.AddEquation
      });
    };

    const setbackMaterials = useMemo(() => {
      const count = Array.isArray(setbackShapes) ? setbackShapes.length : 0;
      return Array.from({ length: count }, () =>
        createSetbackHatchMaterial(DEFAULT_SETBACK_OPACITY, false, false, DEFAULT_SETBACK_COLOR)
      );
    }, [setbackShapes?.length]);
    const setbackMaterialsSelected = useMemo(() => {
      const count = Array.isArray(setbackShapes) ? setbackShapes.length : 0;
      return Array.from({ length: count }, () =>
        createSetbackHatchMaterial(DEFAULT_SETBACK_SELECTED_OPACITY, true, false, DEFAULT_SETBACK_COLOR)
      );
    }, [setbackShapes?.length]);

    let currentMat = null;
    if (selected && selectable) currentMat = matSelected;
    else if (isRoof || isObstacle) currentMat = matRoof;
    else currentMat = matBack;

    const panelBoxClick = (e, pi) => {
      if (!(panelRefs.current[pi] && panelRefs.current[pi].visible)) return;
      if (accordion === 'design' && panelPlacementOnlySelectedRoof && selectable && !panelSelection) {
        changeSelection(e, !selected);
        return;
      }
      e.stopPropagation();
      if (panels.realPanels[pi]) {
        panelChangeSelection(e, pi, !selectedObjects.panels[index]?.includes(pi));
      } else {
        const p = panels.points3D[pi][0];
        const type = panels.points[pi][4];
        insertPanels(index, { x: p.x, y: p.z, z: p.y }, type);
      }
    };

    const panelBoxonPointerEnter = (pi) => {
      if (!panels.realPanels[pi]) {
        setHoveredPanel(pi);
      } else {
        setHoveredPanel(null);
      }
    };

    const panelBoxonPointerLeave = () => {
      setHoveredPanel(null);
    };

    useImperativeHandle(forwardedRef, () => ({
      eave,
      type,
      pitch,
      azimuth,
      eaveHeight,
      mx,
      getPanelCentersWorld: () => {
        const centers = [];
        if (!panelRefs?.current) return centers;
        panelRefs.current.forEach((groupRef) => {
          if (!groupRef) {
            centers.push(null);
            return;
          }

          const panelBox = groupRef.getObjectByName ? groupRef.getObjectByName('panelBox') : null;
          if (!panelBox || !panelBox.geometry) {
            centers.push(null);
            return;
          }
          panelBox.updateWorldMatrix(true, false);
          panelBox.geometry.computeBoundingBox();
          const bbox = panelBox.geometry.boundingBox;
          if (!bbox) {
            centers.push(null);
            return;
          }
          const localCenter = bbox.getCenter(new THREE.Vector3());
          const worldCenter = panelBox.localToWorld(localCenter.clone());
          centers.push(worldCenter);
        });
        return centers;
      }
    }));

    // const rndColor = useMemo(() => {
    //   const rnds = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#FF5722', '#795548', '#607D8B', '#009688', '#4CAF50', '#8BC34A']
    //   const idx = Math.floor(Math.random() * rnds.length);
    //   return rnds[idx];
    // }, [setbacks])

    return (
      <group name={isRoof ? type : ''} index={index}>
        {/* {isRoof && panelShapes && panels  && panels.virtual_points3D && panels.virtual_points3D.map((p, pi) => (
        <mesh key={pi} position={[p.x, p.y, -p.z]}>
          <sphereGeometry args={[0.1, 12, 12]} />
          <meshStandardMaterial color="red" />
        </mesh>
      ))} */}
        {/* {isRoof && setbacks3D && setbacks3D.flat().map((s, si) => (
        <mesh key={si} position={[s.x, s.y, s.z]}>
          <sphereGeometry args={[0.2, 12, 12]} />
          <meshStandardMaterial color={rndColor} />
        </mesh>
      ))} */}
        <mesh
          name="roofPlane"
          index={index}
          ref={mesh}
          castShadow
          receiveShadow
          position={[0, 0, 0]}
          matrixAutoUpdate={false}
          visible={!lineShape}
          onClick={(e) => changeSelection(e, !selected)}
          userData={{ insertingMarker: insertingMarkerObjects }}
        >
          <shapeBufferGeometry attach="geometry" args={[shape]} ref={geometry} />
          {currentMat}
        </mesh>
        {isRoof &&
          panelShapes &&
          panels &&
          panels.projectedPoints.map((p, pi) => {
            const firstSelection = selectedObjects.panels[index]?.length > 0 && selectedObjects.panels[index][0] == pi;
            const inSelected = selectedObjects.panels[index]?.includes(pi);
            const showRevisePrimarySelectionBorder = accordion === 'revise';
            const revisePrimarySelection = showRevisePrimarySelectionBorder && firstSelection;
            const lineColor = panels.realPanels[pi] ? (revisePrimarySelection ? 'red' : 'white') : 'green';
            const lineWidth = panels.realPanels[pi] ? (revisePrimarySelection ? 3 : 2) : 1;
            return (
              <group
                name="panel"
                index={pi}
                userData={{ realPanel: !!panels.realPanels[pi] }}
                visible={
                  showPanels &&
                  (panels.realPanels[pi] || showVirtualPanels) &&
                  (!readonly || panelVisibility || (index in selectedObjects.panels && inSelected))
                }
                ref={(el) => {
                  panelRefs.current[pi] = el;
                }}
                castShadow
                receiveShadow
                matrixAutoUpdate={false}
              >
                <mesh
                  name="panelBox"
                  userData={{ realPanel: !!panels.realPanels[pi] }}
                  rotation={[0, 0, -(panels.azimuthShift * Math.PI) / 180]}
                  onClick={(e) => panelBoxClick(e, pi)}
                  onPointerEnter={() => panelBoxonPointerEnter(pi)}
                  onPointerLeave={() => panelBoxonPointerLeave()}
                >
                  <extrudeGeometry
                    attach="geometry"
                    args={[
                      panelShapes && panelShapes[pi],
                      {
                        steps: 1,
                        depth: 0.2,
                        bevelEnabled: false
                        // UVGenerator: panelUVGenerator,
                      }
                    ]}
                  />
                  {(() => {
                    switch (true) {
                      // Case 1: For real panels that are selected
                      case panels.realPanels[pi] && (panelSelection || readonly) && inSelected:
                        switch (panels.is_valid[pi]) {
                          case 1:
                            return alertedSelectedPanelMat;
                          case 2:
                            return invalidSelectedPanelMat;
                          default:
                            return selectedPanelMat;
                        }
                      // Case 2: For virtual panels that are hovered
                      case !panels.realPanels[pi] && hoveredPanel == pi:
                        return selectedVirtualPanelMat;
                      // Case 3: For real panels (not selected)
                      case panels.realPanels[pi]:
                        switch (panels.is_valid[pi]) {
                          case 1:
                            return alertedPanelMat;
                          case 2:
                            return invalidPanelMat;
                          default:
                            return panelMat;
                        }
                      // Case 4: Default case for virtual panels (not hovered)
                      default:
                        return virtualPanelMat;
                    }
                  })()}
                </mesh>
                <line rotation={[0, 0, -(panels.azimuthShift * Math.PI) / 180]}>
                  <extrudeGeometry
                    attach="geometry"
                    args={[panelShapes && panelShapes[pi], { steps: 1, depth: 0.2, bevelEnabled: false }]}
                  />
                  <lineBasicMaterial
                    attach="material"
                    color={lineColor}
                    transparent
                    opacity={panels.realPanels[pi] ? 0.3 : 1.0}
                    linewidth={lineWidth}
                  />
                </line>
                {/* 
          <line rotation={[0, 0, -(panels.azimuthShift * Math.PI) / 180]}>
            <extrudeGeometry attach="geometry" args={[panelShapes && panelShapes[pi], { steps: 1, depth: 0.2, bevelEnabled: false }]} />
            <lineDashedMaterial 
              attach="material" 
              color={panels.realPanels[pi] ? "white" : "green"} 
              transparent 
              opacity={panels.realPanels[pi] ? 0.3 : 0.1} 
              linewidth={panels.realPanels[pi] ? 2 : 1}
              dashSize={0.5}
              gapSize={0.3}
            />
          </line> */}
              </group>
            );
          })}
        {setbackShapes &&
          setbackShapes.map((sb, sbi) => (
            <group
              key={`${index}-setback-${sbi}`}
              name="setback"
              visible={showSetbacks}
              ref={(el) => {
                setBackRefs.current[sbi] = el;
              }}
              position={[0, 0, 0]}
              matrixAutoUpdate={false}
            >
              <mesh renderOrder={2} receiveShadow raycast={() => null}>
                <shapeBufferGeometry attach="geometry" args={[sb]} />
                <meshBasicMaterial
                  attach="material"
                  color={DEFAULT_SETBACK_COLOR}
                  colorWrite={false}
                  depthTest
                  depthWrite
                  side={THREE.DoubleSide}
                />
              </mesh>

              <mesh
                renderOrder={3}
                receiveShadow
                onClick={(e) => {
                  e.stopPropagation();
                  setbackChangeSelection(e, sbi, !selectedObjects.fireSetbacks[index]?.includes(sbi));
                }}
              >
                <shapeBufferGeometry attach="geometry" args={[sb]} />
                <primitive
                  attach="material"
                  object={
                    selectedObjects.fireSetbacks[index]?.includes(sbi)
                      ? setbackMaterialsSelected[sbi]
                      : setbackMaterials[sbi]
                  }
                />
              </mesh>
            </group>
          ))}
      </group>
    );
  }
);

export default Roof;
